import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import moment from "moment";
import { PAGE_TYPE } from "../../../types/pages.type";
import { buildDashboardDateRangeFromPatientList } from "../../../services/page-converter-navigation.service";

function formatPageFilterRange(filter) {
	if (!filter?.startDate || !filter?.endDate) return null;
	const start = moment(filter.startDate);
	const end = moment(filter.endDate);
	if (!start.isValid() || !end.isValid()) return null;
	return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
}

function getConverterCopy(converterPageType) {
	switch (converterPageType) {
		case PAGE_TYPE.COMMUNITY_TRANSFER:
			return {
				intro:
					"Use your current Community Transfer date range and listed transfers to open Admissions with a filter based on each record's last admission before transfer, when the API provides lastAdmissionDate.",
				dateRangeLabel: "Transfer date range",
				recordsLabel: "Transfers in view",
			};
		case PAGE_TYPE.DECEASED:
			return {
				intro:
					"Use your current Deceased date range and listed records to open Admissions with a filter based on each record's last admission date, when the API provides lastAdmissionDate.",
				dateRangeLabel: "Date range",
				recordsLabel: "Records in view",
			};
		case PAGE_TYPE.OVERALL:
			return {
				intro:
					"Use your current Overall Total Outgoing date range and listed transfers to open Admissions with a filter based on each record's last admission before transfer, when the API provides lastAdmissionDate.",
				dateRangeLabel: "Date range",
				recordsLabel: "Outgoing transfers in view",
			};
		case PAGE_TYPE.HOSPITAL:
		default:
			return {
				intro:
					"Use your current Hospital filters to jump to Admissions with a date range based on when each resident was last admitted before their hospitalization.",
				dateRangeLabel: "Hospital date",
				recordsLabel: "Hospitalizations in view",
			};
	}
}

export function getAdmissionDateBoundsFromPatientList(list) {
	const range = buildDashboardDateRangeFromPatientList(list);
	if (!range) {
		return { earliest: null, latest: null };
	}
	return {
		earliest: moment(range.startDate).clone().startOf("day").format("MMM D, YYYY"),
		latest: moment(range.endDate).clone().startOf("day").format("MMM D, YYYY"),
	};
}

/** @deprecated Use getAdmissionDateBoundsFromPatientList */
export const getAdmissionDateBoundsFromHospitalList = getAdmissionDateBoundsFromPatientList;

/**
 * Shared dialog: convert current dashboard list + date range to Admissions using lastAdmissionDate on rows.
 */
export default function PageConverterDialog({
	open,
	onClose,
	filter,
	patientList = [],
	onGo,
	converterPageType = PAGE_TYPE.HOSPITAL,
}) {
	const copy = getConverterCopy(converterPageType);
	const rangeLabel = formatPageFilterRange(filter);
	const recordCount = patientList.length;
	const { earliest, latest } = getAdmissionDateBoundsFromPatientList(patientList);
	const converterRange = buildDashboardDateRangeFromPatientList(patientList);
	const canNavigate = Boolean(converterRange);

	const handleGo = () => {
		if (!converterRange || typeof onGo !== "function") return;
		onGo(converterRange);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Convert to Admissions Page</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" paragraph>
					{copy.intro}
				</Typography>
				<Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2, "& li": { mb: 0.75 } }}>
					<li>
						<Typography variant="body2">
							<strong>{copy.dateRangeLabel}</strong>
							{rangeLabel ? `: ${rangeLabel}` : " is not set."}
						</Typography>
					</li>
					<li>
						<Typography variant="body2">
							<strong>{copy.recordsLabel}:</strong> {recordCount}
						</Typography>
					</li>
					{/* <li>
						<Typography variant="body2">
							<strong>Admission dates to use</strong> (from last admission before the event,{" "}
							<Box component="span" sx={{ fontFamily: "monospace", fontSize: "0.9em" }}>
								lastAdmissionDate
							</Box>
							):
						</Typography>
					</li> */}
				</Box>
				<Box
					sx={{
						pl: 2,
						borderLeft: "3px solid",
						borderColor: "primary.main",
						mb: 2,
					}}
				>
					<Typography variant="body2" gutterBottom>
						<strong>Earliest admission date:</strong> {earliest ?? "—"}
					</Typography>
					<Typography variant="body2">
						<strong>Latest admission date:</strong> {latest ?? "—"}
					</Typography>
				</Box>
				<Typography variant="body2" color="text.secondary">
					After you choose <strong>Go</strong>, you will be moved to the Admissions page with{" "}
					<strong>Admission date</strong> set from earliest to latest (when available). The Admissions list
					will include all admissions in that range; Transfer status can show how many ended in
					hospitalizations (behavior to be wired next).
				</Typography>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onClose} color="inherit">
					Cancel
				</Button>
				<Button variant="contained" onClick={handleGo} disabled={!canNavigate}>
					Go
				</Button>
			</DialogActions>
		</Dialog>
	);
}
